import React from 'react';
import TimeIcon from './TimeIcon';
import CalenderIcon from './CalenderIcon';
import ContactIcon from './ContactIcon';
import { getFormattedGMTOffset } from '../utils';
import { LivestreamingRoomData } from '@/types';

interface SectionHeadingProps {
  heading?: string | null;
  subheading?: string;
  className?: string;
  roomData?: LivestreamingRoomData | null;
}

const EventInfo: React.FC<{ roomData: LivestreamingRoomData | null }> = ({ roomData }) => {
  if (!roomData) return null;

  const { start_time, end_time, church_name } = roomData;

  if (!church_name && !start_time && !end_time) return null;

  let formattedDate: string | null = null;
  let formattedStartTime: string | null = null;
  let formattedEndTime: string | null = null;
  let timeZoneOffset: string | null = null;
  // New variable to track if event spans multiple days
  let isMultiDay: boolean = false;

  if (start_time) {
    const start = new Date(start_time);
    const dateFormatter = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    // Format for combined date-time display when needed
    const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    formattedDate = dateFormatter.format(start);
    formattedStartTime = start.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    timeZoneOffset = getFormattedGMTOffset(start);

    // Check if event spans multiple days
    if (end_time) {
      const end = new Date(end_time);
      isMultiDay =
        start.getDate() !== end.getDate() ||
        start.getMonth() !== end.getMonth() ||
        start.getFullYear() !== end.getFullYear();

      if (isMultiDay) {
        // Format full date-time for multi-day events
        formattedStartTime = dateTimeFormatter.format(start);
        formattedEndTime = dateTimeFormatter.format(end);
      } else if (end_time) {
        // Format only time for same-day events
        formattedEndTime = end.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
      }
    }
  }

  return (
    <div className="event-info">
      {church_name && (
        <div className="event-location">
          <ContactIcon />
          <span>{church_name}</span>
        </div>
      )}

      {(formattedDate || formattedStartTime || formattedEndTime) && (
        <div className="event-details">
          {/* Only show separate date for single-day events */}
          {!isMultiDay && formattedDate && (
            <p className="event-detail">
              <CalenderIcon />
              <span>{formattedDate}</span>
            </p>
          )}
          {(formattedStartTime || formattedEndTime) && (
            <p className="event-detail">
              <TimeIcon />
              <span>
                {formattedStartTime}
                {formattedStartTime && formattedEndTime ? ' – ' : ''}
                {formattedEndTime} {timeZoneOffset}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const SectionHeading = ({
  heading = '',
  subheading = '',
  className = '',
  roomData = null,
}: SectionHeadingProps) => {
  return (
    <section
      className={`section-heading ${className} ${subheading ? 'has-subheading' : ''}`}
      data-lk-theme="default"
    >
      {heading && <h1 className="title">{heading}</h1>}
      {subheading && <p className="subtitle">{subheading}</p>}
      <EventInfo roomData={roomData} />
    </section>
  );
};

export default SectionHeading;
